import type { LocaleId } from '@bannermatic/core';
import { create } from 'zustand';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  locale: LocaleId;
  /** False for accounts created with Google (no password yet). */
  hasPassword?: boolean;
  google?: boolean;
  /** Listed in SUPERADMIN_EMAILS: admin panel and plan switching without payment. */
  superadmin?: boolean;
}

export interface WorkspaceRef {
  id: string;
  name: string;
  plan: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
}

interface SessionState {
  user: SessionUser | null;
  workspaces: WorkspaceRef[];
  workspaceId: string | null;
  loaded: boolean;
  setSession(user: SessionUser | null, workspaces: WorkspaceRef[]): void;
  setWorkspace(id: string): void;
}

const STORAGE_KEY = 'bm.workspace';

export const useSession = create<SessionState>((set, get) => ({
  user: null,
  workspaces: [],
  workspaceId: localStorage.getItem(STORAGE_KEY),
  loaded: false,
  setSession(user, workspaces) {
    let workspaceId = get().workspaceId;
    if (!workspaces.some((w) => w.id === workspaceId)) workspaceId = workspaces[0]?.id ?? null;
    if (workspaceId) localStorage.setItem(STORAGE_KEY, workspaceId);
    set({ user, workspaces, workspaceId, loaded: true });
  },
  setWorkspace(id) {
    localStorage.setItem(STORAGE_KEY, id);
    set({ workspaceId: id });
  },
}));

export function useCurrentWorkspace(): WorkspaceRef | undefined {
  return useSession((s) => s.workspaces.find((w) => w.id === s.workspaceId));
}

const RANK = { viewer: 0, editor: 1, admin: 2, owner: 3 } as const;
export function useCan(min: WorkspaceRef['role']): boolean {
  const ws = useCurrentWorkspace();
  return ws ? RANK[ws.role] >= RANK[min] : false;
}
